/**
 * WebSocket Manager
 *
 * Manages WebSocket connection lifecycle, event buffering, deduplication,
 * heartbeat, and RTK Query cache invalidation via Socket.IO events.
 */

import { Socket } from 'socket.io-client';
import { addBreadcrumb, captureMessage, withScope, captureException } from '@sentry/react';
import { LRUCache } from '@voley/shared-utils';
import { ServerEvents, ClientEvents } from '@voley/shared-types/socket';
import { baseApi } from './baseApi';
import { apiLogger, reduxLogger } from '@/core/utils/logger';
import { getBootId, getTimeSinceBoot } from '@/core/utils/bootSession';
import { showNotificationToast } from '@/features/notifications';
import { extractErrorMessage } from '@/shared/utils/error';
import { markTeamConversationUnread } from '@/core/store/slices/teamUnreadSlice';

// TYPES

// WebSocket event types
export interface WebSocketEvent {
  id: string;
  type: string;
  data: unknown;
  timestamp: number;
  sequence?: number;
  retry?: boolean;
}

// Event buffer for handling reconnections
export interface EventBuffer {
  events: WebSocketEvent[];
  maxSize: number;
  maxAge: number;
}

// Connection state
export interface ConnectionState {
  isConnected: boolean;
  isReconnecting: boolean;
  lastHeartbeat?: number;
  missedHeartbeats: number;
}

// WEBSOCKET MANAGER

export class WebSocketManager {
  private socket: Socket | null = null;
  private lastSocketId: string | null = null; // Track socket ID to detect reconnections
  private eventBuffer: EventBuffer = {
    events: [],
    maxSize: 100,
    maxAge: 5 * 60 * 1000, // 5 minutes
  };
  private processedEvents = new LRUCache<string>(2000);
  private eventCleanupInterval: NodeJS.Timeout | null = null;
  private readonly EVENT_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
  private eventSequence = 0;
  private connectionState: ConnectionState = {
    isConnected: false,
    isReconnecting: false,
    missedHeartbeats: 0,
  };
  private handlersAttached: boolean = false; // Guard against duplicate handler registration
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private dispatch: unknown = null;
  private store: { getState: () => {
    userProfile?: { profile?: { id?: string } };
    api?: { queries?: Record<string, unknown> };
    messagingUi?: { currentConversationId?: string | null };
    teamsUi?: { currentChannelId?: string | null; currentDirectMessageId?: string | null };
  } } | null = null;
  private currentUserId: string | null = null;

  /**
   * Initialize WebSocket manager with Redux dispatch and store
   */
  initialize(dispatch: unknown, store: { getState: () => {
    userProfile?: { profile?: { id?: string } };
    api?: { queries?: Record<string, unknown> };
    messagingUi?: { currentConversationId?: string | null };
    teamsUi?: { currentChannelId?: string | null; currentDirectMessageId?: string | null };
  } }) {
    this.dispatch = dispatch;
    this.store = store;
    // Get current user ID from store
    const state = store.getState();
    this.currentUserId = state.userProfile?.profile?.id || null;
  }

  /**
   * Connect to WebSocket with event handlers
   */
  connect(socket: Socket) {
    // Skip if exact same socket instance and ID already connected
    if (this.socket === socket && this.lastSocketId === (socket.id ?? null)) {
      reduxLogger.info('[WebSocketManager] Same socket already connected, skipping setup');
      return;
    }

    // Same socket instance but different ID = Socket.IO reconnected
    // Handlers are already registered on this instance, just update the ID reference
    if (this.socket === socket) {
      reduxLogger.info('[WebSocketManager] Socket reconnected with new ID, updating reference');
      this.lastSocketId = socket.id ?? null;
      return;
    }

    // Different socket instance - clean up old handlers and set up new ones
    if (this.socket) {
      reduxLogger.info('[WebSocketManager] Cleaning up previous socket handlers');
      try {
        this.socket.removeAllListeners();
      } catch {
        // Ignore cleanup errors
      }
    }

    this.socket = socket;
    this.lastSocketId = socket.id ?? null;
    this.handlersAttached = false; // Reset guard for new socket instance
    this.setupEventHandlers();
    this.startHeartbeat();
    this.startEventCleanup();
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    this.stopHeartbeat();
    this.stopEventCleanup();
    this.socket = null;
    this.handlersAttached = false;
    this.connectionState.isConnected = false;
  }

  /**
   * Get connection state
   */
  getConnectionState(): ConnectionState {
    return { ...this.connectionState };
  }

  /**
   * Check if in-app notifications are enabled for a given category
   */
  private isInAppEnabledForCategory(category?: string): boolean {
    if (!this.store || !category) return true; // Default to showing if we can't check

    try {
      const state = this.store.getState() as {
        api?: {
          queries?: Record<string, {
            data?: {
              masterEnabled?: boolean;
              categories?: Record<string, { inApp?: boolean }>;
            };
          }>;
        };
      };

      // Find the notification settings in RTK Query cache
      const queries = state.api?.queries || {};
      const settingsQuery = Object.entries(queries).find(([key]) =>
        key.startsWith('getNotificationSettings')
      );

      if (!settingsQuery || !settingsQuery[1]?.data) {
        apiLogger.warn('[WebSocketManager] No notification settings found in cache, defaulting to show');
        return true; // Default to showing if settings not loaded
      }

      const settings = settingsQuery[1].data;

      // Check masterEnabled first
      if (settings.masterEnabled === false) {
        return false;
      }

      // Check category-specific inApp setting
      const categorySettings = settings.categories?.[category];
      if (categorySettings && categorySettings.inApp === false) {
        // Superset rule: assigned_client_messages inherits from all_client_messages
        // (matches backend determineDeliveryChannels semantics)
        if (category === 'assigned_client_messages') {
          const allSettings = settings.categories?.['all_client_messages'];
          if (allSettings?.inApp === true) {
            return true;
          }
        }
        return false;
      }

      return true; // Default to showing if not explicitly disabled
    } catch (error) {
      apiLogger.error('[WebSocketManager] Error checking notification settings:', error);
      return true; // Default to showing on error
    }
  }

  /**
   * Check if the user is currently viewing a specific conversation
   * Used to suppress notifications for the active conversation
   */
  private isViewingConversation(conversationId?: string): boolean {
    if (!this.store || !conversationId) return false;

    try {
      const state = this.store.getState();

      // Check inbox/messaging UI - only suppress if user is on inbox page
      const currentConversationId = state.messagingUi?.currentConversationId;
      if (currentConversationId === conversationId) {
        // Also verify user is actually on inbox page (state persists across navigations)
        const isOnInboxPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard');
        if (isOnInboxPage) {
          return true;
        }
      }

      // Check team chat UI (channels and direct messages) - only suppress if user is on teams page
      // Important: teamsUi state persists across page navigations, so we must check the current URL
      const isOnTeamsPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/teams');
      if (isOnTeamsPage) {
        const teamsUiChannelId = state.teamsUi?.currentChannelId;
        const teamsUiDirectMessageId = state.teamsUi?.currentDirectMessageId;
        if (teamsUiChannelId === conversationId || teamsUiDirectMessageId === conversationId) {
          return true;
        }
      }

      return false;
    } catch (error) {
      apiLogger.error('[WebSocketManager] Error checking current conversation:', error);
      return false;
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers() {
    if (!this.socket) return;
    if (this.handlersAttached) return;
    this.handlersAttached = true;

    // Connection events
    this.socket.on('connect', () => {
      reduxLogger.info('[WebSocketManager] Connected', this.connectionState.isReconnecting ? '(reconnect)' : '(initial)');
      this.connectionState.isConnected = true;
      this.connectionState.isReconnecting = false;
      this.connectionState.missedHeartbeats = 0;

      // Process buffered events
      this.processBufferedEvents();
    });

    this.socket.on('disconnect', (reason) => {
      reduxLogger.info('[WebSocketManager] Disconnected:', reason);
      this.connectionState.isConnected = false;

      // Start reconnection process
      if (reason !== 'io client disconnect') {
        this.startReconnection();

        // Capture unexpected disconnections to Sentry (not user-initiated)
        // Use breadcrumb for transport errors, warning for server disconnects
        if (reason === 'transport error' || reason === 'transport close') {
          addBreadcrumb({
            category: 'websocket',
            message: `WebSocket disconnected: ${reason}`,
            level: 'warning',
            data: {
              reason,
              bootId: getBootId(),
              timeSinceBoot: getTimeSinceBoot(),
            },
          });
        } else if (reason === 'io server disconnect') {
          // Server actively disconnected us - worth capturing
          captureMessage('WebSocket: Server disconnected client', {
            level: 'warning',
            tags: {
              disconnect_reason: reason,
              boot_id: getBootId(),
            },
            extra: {
              timeSinceBoot: getTimeSinceBoot(),
            },
          });
        }
      }
    });

    // Heartbeat events
    this.socket.on(ServerEvents.PONG, () => {
      this.connectionState.lastHeartbeat = Date.now();
      this.connectionState.missedHeartbeats = 0;
    });

    // Data events - Map to RTK Query cache updates
    this.setupDataEventHandlers();
  }

  /**
   * Setup handlers for data events that update RTK Query cache
   */
  private setupDataEventHandlers() {
    if (!this.socket || !this.dispatch) return;

    // Presence events
    this.socket.on(ServerEvents.PRESENCE_UPDATE, (data) => {
      this.handleEvent(ServerEvents.PRESENCE_UPDATE, data, () => {
        reduxLogger.info('[WebSocketManager] Presence update:', data);
        // Invalidate presence-related queries only
        // Note: TeamMembers removed - presence updates don't change the member list,
        // and usePresenceUpdates already handles presence data optimistically
        (this.dispatch as (action: unknown) => void)(baseApi.util.invalidateTags(['Presence', 'PresenceState']));
      });
    });

    this.socket.on(ServerEvents.USER_ONLINE, (data) => {
      this.handleEvent(ServerEvents.USER_ONLINE, data, () => {
        reduxLogger.info('[WebSocketManager] User online:', data);
        (this.dispatch as (action: unknown) => void)(baseApi.util.invalidateTags(['Presence', 'PresenceState']));
      });
    });

    this.socket.on(ServerEvents.USER_OFFLINE, (data) => {
      this.handleEvent(ServerEvents.USER_OFFLINE, data, () => {
        reduxLogger.info('[WebSocketManager] User offline:', data);
        (this.dispatch as (action: unknown) => void)(baseApi.util.invalidateTags(['Presence', 'PresenceState']));
      });
    });

    // Message events
    // NOTE: message_created is handled by SocketProvider with more sophisticated logic
    // (unread count management, preview updates, deduplication with optimistic updates).
    // We intentionally do NOT handle it here to avoid duplicate cache invalidations
    // which cause messages to flicker/disappear. See SocketProvider.tsx for the handler.

    // Team events
    this.socket.on(ServerEvents.TEAM_MEMBER_ADDED, (data) => {
      this.handleEvent(ServerEvents.TEAM_MEMBER_ADDED, data, () => {
        (this.dispatch as (action: unknown) => void)(baseApi.util.invalidateTags(['TeamMembers']));
      });
    });

    this.socket.on(ServerEvents.TEAM_MEMBER_REMOVED, (data) => {
      this.handleEvent(ServerEvents.TEAM_MEMBER_REMOVED, data, () => {
        (this.dispatch as (action: unknown) => void)(baseApi.util.invalidateTags(['TeamMembers']));
      });
    });

    // Notification events
    this.socket.on(ServerEvents.NOTIFICATION_NEW, (data) => {
      this.handleEvent(ServerEvents.NOTIFICATION_NEW, data, () => {
        // New notification arrived: refresh feed and unread count
        (this.dispatch as (action: unknown) => void)(
          baseApi.util.invalidateTags(['Notifications', 'NotificationCount'])
        );

        // Show in-app toast for the notification (only if in-app is enabled for this category)
        const notification = (data as { notification?: { id?: string; title?: string; body?: string; category?: string; metadata?: Record<string, string> } })?.notification;
        if (notification?.title) {
          // Check if in-app notifications are enabled for this category
          const shouldShowInApp = this.isInAppEnabledForCategory(notification.category);

          // Check if user is currently viewing the conversation this notification is for
          const notificationConversationId = notification.metadata?.conversationId;
          const isViewingActiveConversation = this.isViewingConversation(notificationConversationId);

          // For team_chat notifications, update the unread state in Redux slice
          if (notification.category === 'team_chat' && notificationConversationId && !isViewingActiveConversation) {
            (this.dispatch as (action: unknown) => void)(
              markTeamConversationUnread(notificationConversationId)
            );
          }

          // Only show toast if in-app is enabled AND user is not viewing that conversation.
          // To prevent duplicate toasts across multiple tabs, only the focused tab shows the toast.
          const isTabFocused = typeof document !== 'undefined' && document.visibilityState === 'visible';
          if (shouldShowInApp && !isViewingActiveConversation && isTabFocused) {
            showNotificationToast({
              id: notification.id,
              title: notification.title,
              body: notification.body,
              category: notification.category as 'all_client_messages' | 'assigned_client_messages' | 'team_chat' | 'assignments' | 'system_errors' | 'security_alerts' | 'marketing' | 'billing',
              metadata: notification.metadata as { conversationId?: string; messageId?: string; contactId?: string; senderId?: string },
            });
          }
        }
      });
    });

    this.socket.on(ServerEvents.NOTIFICATION_UNREAD_COUNT_UPDATED, (data) => {
      this.handleEvent(ServerEvents.NOTIFICATION_UNREAD_COUNT_UPDATED, data, () => {
        // Only unread count changed
        (this.dispatch as (action: unknown) => void)(
          baseApi.util.invalidateTags(['NotificationCount'])
        );
      });
    });
  }

  /**
   * Handle WebSocket event with deduplication
   */
  private handleEvent(type: string, data: unknown, handler: () => void) {
    // Try to get ID from various possible locations in the data structure
    const dataObj = data as { id?: string; notification?: { id?: string }; message?: { id?: string } };
    const eventId = dataObj?.id || dataObj?.notification?.id || dataObj?.message?.id || `${type}_${Date.now()}_${Math.random()}`;

    // Check if already processed
    if (this.processedEvents.has(eventId)) {
      return;
    }

    // Mark as processed
    this.processedEvents.add(eventId);

    // Execute handler
    try {
      handler();
    } catch (error) {
      reduxLogger.error(`[WebSocketManager] Error handling ${type}:`, error);

      // Capture handler errors to Sentry - these indicate bugs in event processing
      withScope((scope) => {
        scope.setTag('websocket.event_type', type);
        scope.setTag('boot_id', getBootId());
        scope.setFingerprint(['websocket-handler-error', type]);
        scope.setExtra('event_id', eventId);
        scope.setExtra('time_since_boot', getTimeSinceBoot());

        if (error instanceof Error) {
          captureException(error);
        } else {
          captureMessage(`WebSocket handler error for ${type}: ${extractErrorMessage(error)}`, 'error');
        }
      });
    }
  }

  /**
   * Process buffered events
   */
  private processBufferedEvents() {
    const now = Date.now();

    // Remove old events
    this.eventBuffer.events = this.eventBuffer.events.filter(
      event => now - event.timestamp < this.eventBuffer.maxAge
    );

    // Process remaining events
    this.eventBuffer.events.forEach(event => {
      // Process event based on type
      reduxLogger.info('[WebSocketManager] Processing buffered event:', event.type);
    });

    // Clear buffer
    this.eventBuffer.events = [];
  }

  /**
   * Start heartbeat mechanism
   */
  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit(ClientEvents.PING);
        this.connectionState.missedHeartbeats++;

        // Check for missed heartbeats
        if (this.connectionState.missedHeartbeats > 2) {
          reduxLogger.warn('[WebSocketManager] Too many missed heartbeats, reconnecting');

          // Capture missed heartbeats to Sentry - indicates connection issues
          captureMessage('WebSocket: Missed heartbeats, forcing reconnect', {
            level: 'warning',
            tags: {
              missed_heartbeats: String(this.connectionState.missedHeartbeats),
              boot_id: getBootId(),
            },
            extra: {
              timeSinceBoot: getTimeSinceBoot(),
              lastHeartbeat: this.connectionState.lastHeartbeat,
            },
          });

          this.socket.disconnect();
        }
      }
    }, 30000); // 30 seconds
  }

  /**
   * Stop heartbeat mechanism
   */
  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Start periodic cleanup of old processed events
   * Prevents memory buildup from stale event IDs
   */
  private startEventCleanup() {
    this.stopEventCleanup();
    this.eventCleanupInterval = setInterval(() => {
      const removed = this.processedEvents.removeExpired(this.EVENT_MAX_AGE_MS);
      if (removed > 0) {
        reduxLogger.debug(`[WebSocketManager] Cleaned up ${removed} expired event IDs`);
      }
    }, this.EVENT_MAX_AGE_MS); // Run every 5 minutes
  }

  /**
   * Stop event cleanup interval
   */
  private stopEventCleanup() {
    if (this.eventCleanupInterval) {
      clearInterval(this.eventCleanupInterval);
      this.eventCleanupInterval = null;
    }
  }

  /**
   * Mark reconnection state. Actual reconnection is handled by
   * SocketClient (3-attempt cap) + SocketProvider (single-flight + version gating).
   */
  private startReconnection() {
    if (this.connectionState.isReconnecting) return;
    this.connectionState.isReconnecting = true;
    reduxLogger.info('[WebSocketManager] Reconnection delegated to SocketClient/SocketProvider');
  }
}
